import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Scissors, Lightbulb, Paintbrush, Wrench, ArrowRight, Star, PenTool, Zap, Printer, Mail } from "lucide-react";
import FunkyGraphics from "@/components/FunkyGraphics";
import { GalleryLightbox } from "@/components/GalleryLightbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Scissors: Scissors,
  Lightbulb: Lightbulb,
  Paintbrush: Paintbrush,
  Wrench: Wrench,
  PenTool: PenTool,
  Zap: Zap,
  Printer: Printer,
};

// Floating geometric shapes component
const FloatingShapes = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-20 left-[10%] w-20 h-20 bg-[var(--obvious-coral)] rounded-full opacity-60 floating" />
    <div className="absolute top-40 right-[15%] w-16 h-16 bg-[var(--obvious-teal)] rotate-45 opacity-60 floating" style={{ animationDelay: '1s' }} />
    <div className="absolute bottom-32 left-[20%] w-12 h-12 bg-[var(--obvious-yellow)] opacity-60 floating" style={{ animationDelay: '2s' }} />
    <div className="absolute top-60 left-[5%] w-8 h-8 bg-[var(--obvious-purple)] rounded-full opacity-40 floating" style={{ animationDelay: '0.5s' }} />
    <div className="absolute bottom-20 right-[10%] w-24 h-24 border-8 border-[var(--obvious-coral)] rounded-full opacity-40 spin-slow" />
    {/* Stars */}
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="star"
        style={{
          top: `${Math.random() * 80 + 10}%`,
          left: `${Math.random() * 90 + 5}%`,
          animationDelay: `${Math.random() * 2}s`,
        }}
      />
    ))}
  </div>
);

// Marquee component
const Marquee = () => {
  const text = "★ ART ★ PRINTS ★ ONE-OFFS ★ CUSTOM SIGNS ★ THE OBVIOUS COLLECTIVE ★ VISIBLE AS F*CK ★ ";
  
  return (
    <div className="marquee-container bg-[var(--obvious-yellow)] py-4">
      <div className="marquee-content">
        <span className="font-['Anton'] text-2xl md:text-3xl text-[var(--obvious-black)] whitespace-nowrap">
          {text.repeat(4)}
        </span>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, portfolioRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/portfolio`),
        ]);
        setServices(servicesRes.data);
        setPortfolio(portfolioRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const serviceColors = [
    "bg-[var(--obvious-teal)]",
    "bg-[var(--obvious-coral)]",
    "bg-[var(--obvious-purple)]",
    "bg-[var(--obvious-yellow)]",
    "bg-[var(--obvious-teal)]",
    "bg-[var(--obvious-coral)]",
  ];

  return (
    <main className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="min-h-[90vh] relative bg-[var(--obvious-white)] flex items-center overflow-hidden"
        data-testid="hero-section"
      >
        <FloatingShapes />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="font-['Space_Mono'] text-lg bg-[var(--obvious-coral)] text-white px-4 py-2 border-4 border-[var(--obvious-black)]">
                  THE OBVIOUS COLLECTIVE
                </span>
              </div>
              
              <h1 className="font-['Anton'] text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-[var(--obvious-black)]">
                ART.
                <br />
                <span className="text-[var(--obvious-purple)]">PRINTS.</span>
                <br />
                SIGNS.
              </h1>
              
              <p className="font-['Space_Mono'] text-lg md:text-xl text-gray-700 max-w-lg">
                We're a <strong>collective of artists, printers &amp; sign-slingers</strong> making loud, 
                bold, stand-out work. Prints, one-offs, custom art and bespoke signage – printed, cut, 
                sprayed and finished under one roof. We'll design from scratch when you need us to, or 
                run with your artwork when you don't.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="btn-obvious btn-coral inline-flex items-center gap-2"
                  data-testid="hero-cta-quote"
                >
                  COMMISSION A PIECE <ArrowRight size={24} strokeWidth={3} />
                </Link>
                <Link
                  to="/shop"
                  className="btn-obvious btn-yellow inline-flex items-center gap-2"
                  data-testid="hero-cta-shop"
                >
                  SHOP THE COLLECTIVE <ArrowRight size={24} strokeWidth={3} />
                </Link>
              </div>
              <a
                href="mailto:laura@itisobvious.co.uk?subject=Custom%20sign%20enquiry"
                className="btn-obvious bg-[var(--obvious-purple)] text-white inline-flex items-center gap-3 text-2xl md:text-3xl -rotate-1 hover:rotate-0"
                data-testid="hero-cta-email"
              >
                <Mail size={30} strokeWidth={3} /> EMAIL US
                <span className="font-['Space_Mono'] text-xs md:text-sm normal-case opacity-80">we actually reply</span>
              </a>
            </div>
            
            {/* Hero Image */}
            <div className="relative">
              <div className="card-90s p-4 rotate-3 hover:rotate-0 transition-transform">
                <img
                  src="https://customer-assets.emergentagent.com/job_funky-nineties/artifacts/rg31eoj1_Obvious%20banner.png"
                  alt="The Obvious Collective - Art, Prints, Signs"
                  className="w-full h-auto"
                  data-testid="hero-image"
                />
              </div>
              {/* Decorative badge */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-[var(--obvious-teal)] border-4 border-[var(--obvious-black)] rounded-full flex items-center justify-center rotate-12 bounce-custom">
                <span className="font-['Anton'] text-sm text-center leading-tight">
                  MADE<br/>BY HAND
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee />

      {/* Services Section */}
      <section 
        className="py-20 bg-[var(--obvious-black)] relative"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-white)] mb-4">
              WHAT WE MAKE
            </h2>
            <p className="font-['Space_Mono'] text-lg text-gray-400 max-w-2xl mx-auto mb-4">
              Prints, art, signage, one-offs. CNC, LED, spray and screen – if it can be cut, lit, printed or painted, the collective's got a hand on it.
            </p>
            <div className="w-32 h-2 bg-[var(--obvious-coral)] mx-auto" />
          </div>

          {loading ? (
            <div className="flex justify-center gap-2">
              <div className="loading-dot" />
              <div className="loading-dot" />
              <div className="loading-dot" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Star;
                return (
                  <div
                    key={service.id}
                    className={`${serviceColors[index % serviceColors.length]} p-8 border-4 border-[var(--obvious-black)] shadow-[8px_8px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[12px_12px_0px_0px_rgba(255,255,255,0.5)] transition-all hover:-translate-y-2 ${index % 2 === 0 ? 'rotate-1' : '-rotate-1'} hover:rotate-0`}
                    data-testid={`service-card-${service.id}`}
                  >
                    <div className="w-16 h-16 bg-[var(--obvious-black)] border-4 border-[var(--obvious-white)] flex items-center justify-center mb-6">
                      <IconComponent size={32} strokeWidth={3} className="text-white" />
                    </div>
                    <h3 className={`font-['Anton'] text-2xl mb-4 ${index === 2 ? 'text-white' : 'text-[var(--obvious-black)]'}`}>
                      {service.title}
                    </h3>
                    <p className={`font-['Space_Mono'] text-sm ${index === 2 ? 'text-gray-200' : 'text-gray-800'}`}>
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Decorative circles */}
        <div className="absolute top-10 left-10 w-32 h-32 border-8 border-[var(--obvious-purple)] rounded-full opacity-30" />
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-[var(--obvious-coral)] opacity-20 rotate-45" />
      </section>

      {/* Portfolio Section */}
      <section 
        className="py-20 bg-[var(--obvious-white)] striped-bg relative"
        data-testid="portfolio-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-['Space_Mono'] text-lg bg-[var(--obvious-purple)] text-white px-4 py-2 border-4 border-[var(--obvious-black)] inline-block mb-4">
              FROM THE COLLECTIVE
            </span>
            <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-black)]">
              BUILT TO BE SEEN
            </h2>
            <p className="font-['Space_Mono'] text-lg text-gray-600 max-w-2xl mx-auto mt-4">
              Real pieces. Real clients. From one-off commissions to full-blown shop fronts – all made in-house, all a bit obvious.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center gap-2">
              <div className="loading-dot" />
              <div className="loading-dot" />
              <div className="loading-dot" />
            </div>
          ) : (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-8">
              {portfolio.map((item, index) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setActiveIndex(index)}
                  className={`polaroid group cursor-zoom-in block w-full text-left mb-8 break-inside-avoid ${index % 2 === 0 ? 'rotate-1' : '-rotate-1'}`}
                  data-testid={`portfolio-item-${item.id}`}
                >
                  <div className="overflow-hidden">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-4">
                    <span className="font-['Space_Mono'] text-xs bg-[var(--obvious-teal)] text-black px-2 py-1 uppercase">
                      {item.category}
                    </span>
                    <h3 className="font-['Anton'] text-xl mt-2">{item.title}</h3>
                    <p className="font-['Space_Mono'] text-sm text-gray-600">{item.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          <GalleryLightbox items={portfolio} index={activeIndex} onChange={setActiveIndex} />
        </div>
      </section>

      {/* Funky Graphics Section */}
      <FunkyGraphics />

      {/* CTA Section */}
      <section 
        className="py-20 bg-[var(--obvious-purple)] relative overflow-hidden"
        data-testid="cta-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-['Anton'] text-5xl md:text-7xl text-[var(--obvious-white)] mb-6">
            LET'S MAKE SOMETHING
            <br />
            <span className="text-[var(--obvious-yellow)]">OBVIOUS</span>
          </h2>
          <p className="font-['Space_Mono'] text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Got an idea? Good. Want a print off the shelf? Even easier. Whether it's commissioned art, a one-off piece or a 10ft illuminated statement — the collective's listening.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="btn-obvious btn-coral inline-flex items-center gap-2 text-2xl"
              data-testid="cta-button"
            >
              COMMISSION US <ArrowRight size={28} strokeWidth={3} />
            </Link>
            <Link
              to="/shop"
              className="btn-obvious btn-yellow inline-flex items-center gap-2 text-2xl"
              data-testid="cta-shop-button"
            >
              SHOP THE DROP <ArrowRight size={28} strokeWidth={3} />
            </Link>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-40 h-40 border-8 border-[var(--obvious-yellow)] rounded-full opacity-30 spin-slow" />
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-[var(--obvious-coral)] opacity-20" />
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-[var(--obvious-teal)] rotate-45 opacity-30 floating" />
      </section>

      {/* Second Marquee */}
      <div className="marquee-container bg-[var(--obvious-teal)] py-4 border-y-4 border-[var(--obvious-black)]">
        <div className="marquee-content" style={{ animationDirection: 'reverse' }}>
          <span className="font-['Anton'] text-2xl md:text-3xl text-[var(--obvious-black)] whitespace-nowrap">
            {"★ PRINTS. REPRINTS. ONE-OF-A-KINDS. ★ CUSTOM SIGNS. CUSTOM ART. ★ MADE BY HUMANS. SOLD TO BRAVE ONES. ★ ".repeat(4)}
          </span>
        </div>
      </div>
    </main>
  );
};

export default HomePage;
